import type { SubjectTrend } from '@/lib/scores';

interface StudentScoreTrendProps {
  trends: SubjectTrend[];
}

function barHeight(value: number, min: number, range: number): number {
  return 6 + ((value - min) / range) * 12;
}

function Sparkline({ points }: { points: number[] }) {
  const recent = points.slice(-5);
  const max = Math.max(...recent);
  const min = Math.min(...recent);
  const range = max - min || 1;
  const rising = recent.length > 1 && recent[recent.length - 1] >= recent[0];

  return (
    <span className="flex h-5 items-end gap-0.5">
      {recent.map((p, i) => (
        <span
          key={`${p}-${i}`}
          className={`w-1 rounded-sm ${rising ? 'bg-primary-300' : 'bg-accent-300'}`}
          style={{ height: `${barHeight(p, min, range)}px` }}
        />
      ))}
    </span>
  );
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null) {
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-600">
        첫 기록
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-600">
        <i className="ri-subtract-line"></i>0
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
        up ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-800'
      }`}
    >
      <i className={up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
      {Math.abs(delta)}
    </span>
  );
}

export default function StudentScoreTrend({ trends }: StudentScoreTrendProps) {
  return (
    <div className="border-t border-background-200 pt-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-600">
        <i className="ri-line-chart-line text-primary-600"></i>
        과목별 성적 추이
      </p>

      {trends.length === 0 ? (
        <p className="mt-2 text-xs text-foreground-400">아직 기록된 성적이 없어요.</p>
      ) : (
        <div className="mt-2.5 flex flex-col gap-2">
          {trends.map((t) => (
            <div key={t.subject} className="flex items-center gap-2">
              <span className="w-14 shrink-0 truncate text-xs font-medium text-foreground-700">
                {t.subject}
              </span>
              <Sparkline points={t.points} />
              <span className="ml-auto text-sm font-bold text-foreground-950">{t.latest}</span>
              <DeltaBadge delta={t.delta} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}